import { EnhancedStore } from "@reduxjs/toolkit";
import { RootState } from "./store";

type PredicateSelector = (state: RootState) => boolean;

type Selector<T> = (state: RootState) => T;

/**
 * Waits for a specific condition in the Redux store to be true and resolves with a result
 * @param {Object} store - The Redux store
 * @param {Function} condition - Function that takes state and returns boolean when to resolve
 * @param {Function} selector - Function that takes state and returns the value to resolve with
 * @param {Object} options - Optional configuration
 * @param {number} options.timeout - Timeout in ms (default: no timeout)
 * @returns {Promise} Promise that resolves with the selected value when condition is met
 */
export default function whenStateReaches<T>(
  store: EnhancedStore<RootState>,
  condition: PredicateSelector,
  selector: Selector<T>,
  timeout = null
) {
  return new Promise((resolve, reject) => {
    // Check immediately first
    const currentState = store.getState();
    if (condition(currentState)) {
      return resolve(selector(currentState));
    }

    // Set up timeout if specified
    let timeoutId = null;
    if (timeout) {
      timeoutId = setTimeout(() => {
        unsubscribe();
        reject(new Error(`Timeout waiting for condition after ${timeout}ms`));
      }, timeout);
    }

    // Set up Redux subscription
    const unsubscribe = store.subscribe(() => {
      const state = store.getState();
      if (condition(state)) {
        unsubscribe();
        if (timeoutId) clearTimeout(timeoutId);
        resolve(selector(state));
      }
    });
  });
}
